import { useEffect, useState } from "react";
import api, { formatApiError, studentPhoto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, EmptyState, Field } from "@/components/common";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StateSelect } from "@/components/StateSelect";
import { StatusBadge } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "@/components/MultiSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Search } from "lucide-react";
import { DeleteButton } from "@/components/DeleteButton";
import { fmtDate } from "@/lib/dates";

const DISCIPLINES = ["PT", "PTA", "OT", "OTA", "SLP", "Other"];

export default function Students() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [q, setQ] = useState("");
  const [fDisc, setFDisc] = useState([]);
  const [fClear, setFClear] = useState([]);
  const [fPlacement, setFPlacement] = useState([]);
  const [fState, setFState] = useState([]);
  const [fSchool, setFSchool] = useState([]);
  const [fFacility, setFFacility] = useState([]);
  const [fCI, setFCI] = useState([]);
  const [rotFrom, setRotFrom] = useState("");
  const [rotTo, setRotTo] = useState("");
  const isDOR = user.role === "DOR";
  const canEdit = ["Super Admin", "Campus Coordinator", "School User"].includes(user.role);
  const load = () => api.get("/students").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const uniq = (key) => [...new Set(rows.map((r) => r[key]).filter(Boolean))].sort();
  const clearances = uniq("clearance_status");
  const placements = uniq("placement_status");
  // Rotation state (where they're being placed), falling back to where they live.
  const stateOf = (r) => r.placement_state || r.address_state || "";
  const states = [...new Set(rows.map(stateOf).filter(Boolean))].sort();
  const schools = uniq("school_name");
  const facilities = uniq("facility_name");
  const cis = uniq("ci_name");
  const term = q.trim().toLowerCase();
  const has = (list, v) => list.length === 0 || list.includes(v);
  const filtered = rows.filter((s) => {
    if (term && ![s.name, s.school_email, s.personal_email, s.school_name, s.facility_name, s.ci_name]
      .filter(Boolean).some((v) => v.toLowerCase().includes(term))) return false;
    if (!has(fDisc, s.discipline)) return false;
    if (!has(fClear, s.clearance_status)) return false;
    if (!has(fPlacement, s.placement_status)) return false;
    if (!has(fState, stateOf(s))) return false;
    if (!has(fSchool, s.school_name)) return false;
    if (!has(fFacility, s.facility_name)) return false;
    if (!has(fCI, s.ci_name)) return false;
    if (rotFrom && (!s.rotation_start || s.rotation_start.slice(0, 10) < rotFrom)) return false;
    if (rotTo && (!s.rotation_start || s.rotation_start.slice(0, 10) > rotTo)) return false;
    return true;
  });
  const activeFilters = fDisc.length + fClear.length + fPlacement.length + fState.length + fSchool.length + fFacility.length + fCI.length + (rotFrom ? 1 : 0) + (rotTo ? 1 : 0) + (term ? 1 : 0);
  const clearAll = () => { setQ(""); setFDisc([]); setFClear([]); setFPlacement([]); setFState([]); setFSchool([]); setFFacility([]); setFCI([]); setRotFrom(""); setRotTo(""); };

  const openEdit = (s) => { setEditing(s); setForm({ ...s }); };
  const save = async () => {
    try {
      const { id, user_id, created_at, updated_at, ...body } = form;
      await api.patch(`/students/${editing.id}`, body);
      toast.success("Student updated"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title={isDOR ? "Facility Students" : "Students"} subtitle={isDOR ? "Every student placed at your facility" : "Current and incoming interns"} />
      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3" data-testid="student-filters">
          <div className="relative">
            <div className="text-xs text-zinc-400 mb-1">Search</div>
            <Search className="w-4 h-4 text-zinc-400 absolute left-2.5 top-[30px]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, school…"
              className="pl-8 h-9 w-56" data-testid="filter-search" />
          </div>
          <MultiSelect label="Discipline" options={DISCIPLINES} value={fDisc} onChange={setFDisc} width="w-36" testId="filter-discipline" />
          <MultiSelect label="Clearance" options={clearances} value={fClear} onChange={setFClear} width="w-44" testId="filter-clearance" />
          <MultiSelect label="Placement" options={placements} value={fPlacement} onChange={setFPlacement} width="w-48" testId="filter-placement" />
          {isDOR && <MultiSelect label="Clinical Instructor" options={cis} value={fCI} onChange={setFCI} width="w-48" testId="filter-ci" />}
          <MultiSelect label="State" options={states} value={fState} onChange={setFState} width="w-32" testId="filter-state" />
          {!isDOR && user.role !== "School User" && (
            <MultiSelect label="School" options={schools} value={fSchool} onChange={setFSchool} width="w-52" testId="filter-school" />
          )}
          {!isDOR && (
            <MultiSelect label="Facility" options={facilities} value={fFacility} onChange={setFFacility} width="w-52" testId="filter-facility" />
          )}
          <div><div className="text-xs text-zinc-400 mb-1">Rotation start from</div><Input type="date" className="w-40 h-9" value={rotFrom} onChange={(e) => setRotFrom(e.target.value)} data-testid="filter-rot-from" /></div>
          <div><div className="text-xs text-zinc-400 mb-1">to</div><Input type="date" className="w-40 h-9" value={rotTo} onChange={(e) => setRotTo(e.target.value)} data-testid="filter-rot-to" /></div>
          {activeFilters > 0 && <Button variant="outline" size="sm" onClick={clearAll} data-testid="filter-clear">Clear {activeFilters} filter{activeFilters > 1 ? "s" : ""}</Button>}
          <span className="text-xs text-zinc-400 ml-auto" data-testid="student-count">{filtered.length} of {rows.length}</span>
        </div>
      </Card>
      <Card className="overflow-hidden">
        {filtered.length === 0 ? <EmptyState text="No students." /> : (
          <div className="overflow-x-auto cc-scroll -m-5">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                <tr><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Discipline</th>{isDOR ? <><th className="text-left px-5 py-3">Clinical Instructor</th><th className="text-left px-5 py-3">Rotation</th><th className="text-left px-5 py-3">Clearance</th></> : <><th className="text-left px-5 py-3">School</th><th className="text-left px-5 py-3">Email</th><th className="text-left px-5 py-3">State</th><th className="text-left px-5 py-3">Rotation</th><th className="text-left px-5 py-3">Clearance</th><th className="text-left px-5 py-3">Graduation</th><th className="text-left px-5 py-3">Employment</th></>}<th className="px-5 py-3"></th></tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className="border-t" data-testid={`student-${s.id}`}>
                    <td className="px-5 py-3 font-medium text-zinc-800"><div className="flex items-center gap-2.5"><Avatar src={studentPhoto(s.id)} name={s.name} size={30} testId={`student-avatar-${s.id}`} />{s.name}</div></td>
                    <td className="px-5 py-3"><span className="font-mono text-xs px-2 py-0.5 bg-zinc-100 rounded">{s.discipline}</span></td>
                    {isDOR ? (
                      <>
                        <td className="px-5 py-3 text-zinc-600">{s.ci_name || <span className="text-cc-orange text-xs">Not assigned</span>}</td>
                        <td className="px-5 py-3 font-mono text-xs text-zinc-600">{fmtDate(s.rotation_start)}{s.rotation_end ? ` – ${fmtDate(s.rotation_end)}` : ""}</td>
                        <td className="px-5 py-3"><StatusBadge status={s.clearance_status || "Not Started"} /></td>
                      </>
                    ) : (
                      <>
                        <td className="px-5 py-3 text-zinc-600">{s.school_name || "—"}</td>
                        <td className="px-5 py-3 text-zinc-600">{s.school_email || s.personal_email}</td>
                        <td className="px-5 py-3 text-zinc-600">{stateOf(s) || "—"}</td>
                        <td className="px-5 py-3 font-mono text-xs text-zinc-600">{fmtDate(s.rotation_start)}</td>
                        <td className="px-5 py-3">{s.clearance_status ? <StatusBadge status={s.clearance_status} /> : <span className="text-zinc-400 text-xs">—</span>}</td>
                        <td className="px-5 py-3 font-mono text-xs">{fmtDate(s.graduation_date)}</td>
                        <td className="px-5 py-3">{s.employment_interest ? <span className="text-cc-green text-xs font-medium">Interested</span> : <span className="text-zinc-400 text-xs">—</span>}</td>
                      </>
                    )}
                    <td className="px-5 py-3 text-right">{canEdit && <button onClick={() => openEdit(s)} data-testid={`edit-student-${s.id}`} className="text-zinc-400 hover:text-cc-blue mr-3 align-middle"><Pencil className="w-4 h-4 inline" /></button>}{user.role === "Super Admin" && <DeleteButton endpoint={`/students/${s.id}`} label="student" name={s.name} onDeleted={load} testId={`delete-student-${s.id}`} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Student</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name"><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="student-name" /></Field>
            <Field label="Discipline">
              <Select value={form.discipline} onValueChange={(v) => setForm({ ...form, discipline: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DISCIPLINES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Personal email"><Input value={form.personal_email || ""} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} /></Field>
            <Field label="School email"><Input value={form.school_email || ""} onChange={(e) => setForm({ ...form, school_email: e.target.value })} /></Field>
            <Field label="Cell phone"><Input value={form.cell_phone || ""} onChange={(e) => setForm({ ...form, cell_phone: e.target.value })} /></Field>
            <Field label="State"><StateSelect value={form.address_state} onChange={(v) => setForm({ ...form, address_state: v })} /></Field>
            <Field label="Graduation date"><Input type="date" value={form.graduation_date ? form.graduation_date.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, graduation_date: e.target.value })} /></Field>
            <Field label="Employment interest">
              <Select value={String(form.employment_interest)} onValueChange={(v) => setForm({ ...form, employment_interest: v === "true" })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="true">Interested</SelectItem><SelectItem value="false">Not interested</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter><Button onClick={save} className="bg-cc-blue text-white" data-testid="save-student-btn">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
